import { useCallback, useState, type ReactNode } from 'react';
import { SAMPLE_INVESTOR_ID } from '@dealstack/shared/lib/documents/fieldBindingCatalogue';
import { DocumentWorkflowTemplateSourceFormat } from '@dealstack/shared/lib/generated';
import { FieldBindingEditor } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/components/FieldBinding/FieldBindingEditor.tsx';
import { useFieldBinding } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/components/FieldBinding/useFieldBinding.ts';
import { useFieldBindingPreview } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/api/useFieldBindingPreview.ts';
import type { PanelHeaderEdit } from '@dealstack/frontend/shared/components/FieldBinding/TemplateNameField/templateNameField.types.ts';

interface Props {
	templateId: string;
	file: Blob;
	sourceFormat?: DocumentWorkflowTemplateSourceFormat;
	/** Pinned above the tab bar — the document's own name. */
	panelHeader?: ReactNode;
	/** Folds an unsaved {@link panelHeader} edit into the editor's Save. */
	panelHeaderEdit?: PanelHeaderEdit;
	/** Pinned at the top of the side panel — the editor-wide Data points / Signatures bar. */
	paletteTabs?: ReactNode;
	/** Canvas zoom, when a host shares one across surfaces. Uncontrolled otherwise. */
	scale?: number;
	onScaleChange?: (scale: number) => void;
	onClose: () => void;
}

// Owns the deal-scoped data layer (bindings/catalogue/preview) so it's only fetched while the
// field-binding Modal is actually mounted, matching the previous lazy-fetch-on-open behaviour.
export const DealFieldBindingEditor = ({
	templateId,
	file,
	sourceFormat,
	panelHeader,
	panelHeaderEdit,
	paletteTabs,
	scale,
	onScaleChange,
	onClose,
}: Props) => {
	// A Word template goes to its own editor and has no PDF bindings to fetch, so the
	// null templateId keeps both deal-scoped queries disabled.
	const isDocx = sourceFormat === DocumentWorkflowTemplateSourceFormat.DOCX;
	// Mirrors FieldBindingEditor's own preview-investor selection so the catalogue (which is
	// investor-scoped) refetches for whichever investor is currently being previewed.
	const [selectedInvestorId, setSelectedInvestorId] = useState<string | null>(SAMPLE_INVESTOR_ID);
	const binding = useFieldBinding(isDocx ? null : templateId, selectedInvestorId);
	const previewMutation = useFieldBindingPreview();
	const requestPreview = useCallback(
		(investorId: string | null) => {
			if (!investorId) {
				return Promise.reject(new Error('No investor selected for preview'));
			}
			return previewMutation.mutateAsync({ templateId, investorId });
		},
		[templateId, previewMutation]
	);

	return (
		<FieldBindingEditor
			templateId={templateId}
			file={file}
			sourceFormat={sourceFormat}
			binding={binding}
			requestPreview={requestPreview}
			showInvestorPreview
			panelHeader={panelHeader}
			panelHeaderEdit={panelHeaderEdit}
			paletteTabs={paletteTabs}
			scale={scale}
			onScaleChange={onScaleChange}
			initialPreviewInvestorId={selectedInvestorId}
			onPreviewInvestorChange={setSelectedInvestorId}
			onClose={onClose}
		/>
	);
};
